import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Grid3x3, Check } from 'lucide-react'
import { modulesApi, type ModuleMatrixRow } from '@/api/modules'

interface ModuleColumn {
  key: string
  label: string
}

interface OrgRow {
  id: string
  name: string
  active: Set<string>
}

function moduleLabel(row: ModuleMatrixRow): string {
  return row.label_he || row.label_en || row.module_key
}

export default function ModulesMatrixPage() {
  const [search, setSearch] = useState('')

  const { data: rows = [], isLoading, isError, error } = useQuery({
    queryKey: ['admin-modules-matrix'],
    queryFn: () => modulesApi.getModulesMatrix(),
    staleTime: 60_000,
  })

  const { columns, orgs } = useMemo(() => {
    const colMap = new Map<string, string>()
    const orgMap = new Map<string, OrgRow>()

    for (const r of rows) {
      if (!colMap.has(r.module_key)) colMap.set(r.module_key, moduleLabel(r))
      let org = orgMap.get(r.organization_id)
      if (!org) {
        org = { id: r.organization_id, name: r.org_name, active: new Set() }
        orgMap.set(r.organization_id, org)
      }
      if (r.is_active) org.active.add(r.module_key)
    }

    const columns: ModuleColumn[] = Array.from(colMap, ([key, label]) => ({ key, label })).sort(
      (a, b) => a.label.localeCompare(b.label),
    )
    const orgs = Array.from(orgMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    return { columns, orgs }
  }, [rows])

  const filteredOrgs = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return orgs
    return orgs.filter(o => o.name.toLowerCase().includes(q))
  }, [orgs, search])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Modules Matrix</h1>
        <p className="text-sm text-gray-400 mt-1">
          Active modules per organization across the platform
        </p>
      </div>

      {/* Stat chips + search */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <Grid3x3 size={16} style={{ color: '#6366f1' }} />
          <span className="text-gray-400">Organizations</span>
          <span className="font-semibold text-white ml-1">{filteredOrgs.length}</span>
        </div>
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <span className="text-gray-400">Modules</span>
          <span className="font-semibold text-white ml-1">{columns.length}</span>
        </div>
        <div
          className="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <Search size={15} className="text-gray-500 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search organizations…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent text-sm text-white placeholder-gray-500 outline-none flex-1"
          />
        </div>
      </div>

      {/* Matrix */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Grid3x3 size={32} className="text-gray-600" />
            <p className="text-sm text-gray-400">Failed to load matrix</p>
            <p className="text-xs text-gray-500">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        ) : filteredOrgs.length === 0 || columns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Grid3x3 size={32} className="text-gray-600" />
            <p className="text-sm text-gray-500">No data to display</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th
                    className="sticky left-0 top-0 z-30 text-left px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-gray-400 whitespace-nowrap"
                    style={{ background: '#161922', borderBottom: '1px solid #2a2d3e' }}
                  >
                    Organization
                  </th>
                  <th
                    className="sticky top-0 z-20 px-3 py-3 text-[11px] font-medium uppercase tracking-wider text-gray-500 text-center whitespace-nowrap"
                    style={{ background: '#161922', borderBottom: '1px solid #2a2d3e' }}
                  >
                    #
                  </th>
                  {columns.map(col => (
                    <th
                      key={col.key}
                      title={col.key}
                      className="sticky top-0 z-20 px-3 py-3 text-[11px] font-medium text-gray-300 text-center whitespace-nowrap"
                      style={{ background: '#161922', borderBottom: '1px solid #2a2d3e' }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredOrgs.map((org, i) => (
                  <tr
                    key={org.id}
                    className="hover:bg-white/[0.03] transition-colors"
                    style={i < filteredOrgs.length - 1 ? { borderBottom: '1px solid #2a2d3e' } : {}}
                  >
                    <td
                      className="sticky left-0 z-10 px-4 py-2.5 font-medium text-white whitespace-nowrap"
                      style={{ background: '#1a1d27' }}
                    >
                      {org.name}
                    </td>
                    <td className="px-3 py-2.5 text-center text-xs text-gray-400 num">
                      {org.active.size}
                    </td>
                    {columns.map(col => {
                      const on = org.active.has(col.key)
                      return (
                        <td key={col.key} className="px-3 py-2.5 text-center">
                          {on ? (
                            <Check size={15} className="inline" style={{ color: '#4edea3' }} />
                          ) : (
                            <span className="text-gray-700">·</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
